import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const AppCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const screenshots = [
    { src: '/app-screens/screen-01.jpg', title: 'Home Dashboard', description: 'PLAY · EXPLORE · CONNECT', alt: 'PopCrown app home dashboard - PLAY EXPLORE CONNECT - Michael Jackson fan community' },
    { src: '/app-screens/screen-02.jpg', title: 'Welcome Screen', description: 'Personalised greeting', alt: 'PopCrown app welcome screen - Michael Jackson fan app personalised greeting' },
    { src: '/app-screens/screen-03.jpg', title: 'PLAY Hub', description: 'Quizzes, Games, Leaderboard', alt: 'PopCrown PLAY hub - 30+ Michael Jackson quizzes games leaderboard' },
    { src: '/app-screens/screen-05.jpg', title: 'EXPLORE Hub', description: 'News, Music, History, Tourism', alt: 'PopCrown EXPLORE hub - MJ news music history tourism Legal Vault' },
    { src: '/app-screens/screen-06.jpg', title: 'CONNECT', description: 'Inside the community', alt: 'PopCrown CONNECT community feed - Michael Jackson fans worldwide' },
    { src: '/app-screens/screen-07.jpg', title: 'Community Feed', description: 'Posts from fans worldwide', alt: 'PopCrown community feed - real Michael Jackson fan posts and discussions' },
    { src: '/app-screens/screen-08.jpg', title: 'Daily Quiz', description: 'Earn points, climb tiers', alt: 'PopCrown daily Michael Jackson quiz - earn points climb 20 tiers' },
    { src: '/app-screens/screen-09.jpg', title: 'Leaderboard', description: 'Compete globally', alt: 'PopCrown global leaderboard - Michael Jackson fans compete worldwide' },
    { src: '/app-screens/screen-17-legal-vault.jpg', title: 'Legal Vault', description: 'The Search for Truth — 81 documents', alt: 'PopCrown Legal Vault - Michael Jackson FBI files court documents 0 convictions 14 not guilty verdicts truth' },
    { src: '/app-screens/screen-18-in-his-own-words.jpg', title: 'In His Own Words', description: 'Michael\u2019s verified quotes by topic', alt: 'PopCrown In His Own Words - searchable Michael Jackson quotes interviews poetry art statements verified database' },
    { src: '/app-screens/screen-19-mj-journey.jpg', title: 'MJ Journey', description: '44 locations worldwide', alt: 'PopCrown MJ Journey - 44 Michael Jackson tourism locations worldwide map Gary birthplace Neverland trip plans' },
    { src: '/app-screens/screen-20-michael-biopic.jpg', title: 'Michael (2026)', description: 'Biopic hub — trailers, quiz, $1B+', alt: 'PopCrown Michael 2026 biopic hub - official trailers movie quiz box office details Antoine Fuqua MJ film $1 billion worldwide' },
    { src: '/app-screens/screen-22-wwmd.jpg', title: 'WWMD Programme', description: 'What Would Michael Do? — 90 days', alt: 'PopCrown What Would Michael Do programme 90 day journey Mirror Move Make Care Practical Curiosity Before Reaction Exit With Dignity humanitarian principles' },
    { src: '/app-screens/screen-12.jpg', title: 'Timeline', description: 'Complete history 1958\u20132009', alt: 'PopCrown Michael Jackson complete timeline 1958-2009 King of Pop' },
    { src: '/app-screens/screen-13.jpg', title: 'Support Rooms', description: 'Safe, moderated peer support', alt: 'PopCrown support rooms - mental health bereavement neurodivergence peer support' },
    { src: '/app-screens/screen-21-annie-ai.jpg', title: 'Annie AI', description: 'Your MJ Super-Fan Guide', alt: 'PopCrown Annie AI - your Michael Jackson super-fan guide AI assistant unlimited for PopCrown Community members verified MJ facts' },
    { src: '/app-screens/screen-15.jpg', title: 'Profile', description: 'Your fan identity', alt: 'PopCrown profile - Michael Jackson fan identity points tier badges' },
    { src: '/app-screens/screen-16.jpg', title: 'Daily News', description: 'Latest MJ headlines', alt: 'PopCrown daily Michael Jackson news headlines biopic 2026 updates' }
  ];

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % screenshots.length);
  }, [screenshots.length]);

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + screenshots.length) % screenshots.length);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  // Auto-play
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(nextSlide, 2500);
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  // Get visible slides (5 at a time for the effect)
  const getVisibleSlides = () => {
    const slides = [];
    for (let i = -2; i <= 2; i++) {
      const index = (currentIndex + i + screenshots.length) % screenshots.length;
      slides.push({ ...screenshots[index], position: i, originalIndex: index });
    }
    return slides;
  };

  const visibleSlides = getVisibleSlides();

  return (
    <div className="relative w-full py-8">
      {/* Carousel Container */}
      <div 
        className="relative h-[560px] md:h-[660px] flex items-center justify-center overflow-hidden"
        onMouseEnter={() => setIsAutoPlaying(false)}
        onMouseLeave={() => setIsAutoPlaying(true)}
      >
        {visibleSlides.map((slide, idx) => {
          const isCenter = slide.position === 0;
          const isAdjacent = Math.abs(slide.position) === 1;
          const isOuter = Math.abs(slide.position) === 2;

          // Calculate transforms based on position
          let translateX = slide.position * 220;
          let scale = isCenter ? 1 : isAdjacent ? 0.75 : 0.5;
          let opacity = isCenter ? 1 : isAdjacent ? 0.7 : 0.4;
          let zIndex = isCenter ? 30 : isAdjacent ? 20 : 10;
          let blur = isCenter ? 0 : isAdjacent ? 0 : 2;

          // Adjust for mobile
          if (typeof window !== 'undefined' && window.innerWidth < 768) {
            translateX = slide.position * 120;
            scale = isCenter ? 1 : isAdjacent ? 0.65 : 0;
            opacity = isCenter ? 1 : isAdjacent ? 0.5 : 0;
          }

          return (
            <div
              key={`${slide.originalIndex}-${idx}`}
              className="absolute cursor-pointer transition-all duration-500 ease-out"
              style={{
                transform: `translateX(${translateX}px) scale(${scale})`,
                opacity,
                zIndex,
                filter: blur > 0 ? `blur(${blur}px)` : 'none',
              }}
              onClick={() => goToSlide(slide.originalIndex)}
            >
              {/* Phone Frame */}
              <div 
                className="relative rounded-[2.5rem] overflow-hidden"
                style={{
                  width: '260px',
                  height: '565px',
                  background: 'linear-gradient(145deg, #2a2a2a 0%, #1a1a1a 100%)',
                  padding: '8px',
                  boxShadow: isCenter 
                    ? '0 25px 80px rgba(225, 6, 0, 0.4), 0 15px 40px rgba(220, 20, 60, 0.3), 0 0 0 2px rgba(225, 6, 0, 0.5)' 
                    : '0 15px 40px rgba(0, 0, 0, 0.4)',
                }}
              >
                {/* Screen */}
                <div className="w-full h-full rounded-[2rem] overflow-hidden bg-black">
                  <picture>
                    {slide.webp && <source srcSet={slide.webp} type="image/webp" />}
                    <img
                      src={slide.src}
                      sizes={slide.sizes || '260px'}
                      alt={slide.alt || `PopCrown Michael Jackson fan app - ${slide.title} - ${slide.description} - MJ fan community feature King of Pop`}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                      decoding="async"
                      fetchPriority={isCenter ? 'high' : 'low'}
                      width="260"
                      height="563"
                    />
                  </picture>
                </div>
                
                {/* Notch */}
                <div 
                  className="absolute top-3 left-1/2 transform -translate-x-1/2 w-24 h-6 bg-black rounded-full"
                  style={{ boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)' }}
                />
              </div>

              {/* Label - Only show for center */}
              {isCenter && (
                <div className="absolute -bottom-16 left-1/2 transform -translate-x-1/2 text-center w-64">
                  <p className="text-xl font-bold text-[#E10600] mb-1">{slide.title}</p>
                  <p className="text-sm text-gray-400">{slide.description}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        aria-label="Previous screenshot"
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-40 p-3 rounded-full transition-all hover:scale-110"
        style={{
          background: 'linear-gradient(135deg, rgba(225, 6, 0, 0.2) 0%, rgba(220, 20, 60, 0.2) 100%)',
          border: '2px solid rgba(225, 6, 0, 0.5)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
        }}
      >
        <ChevronLeft className="w-6 h-6 text-[#E10600]" aria-hidden="true" />
      </button>
      <button
        onClick={nextSlide}
        aria-label="Next screenshot"
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-40 p-3 rounded-full transition-all hover:scale-110"
        style={{
          background: 'linear-gradient(135deg, rgba(225, 6, 0, 0.2) 0%, rgba(220, 20, 60, 0.2) 100%)',
          border: '2px solid rgba(225, 6, 0, 0.5)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
        }}
      >
        <ChevronRight className="w-6 h-6 text-[#E10600]" aria-hidden="true" />
      </button>

      {/* Dot Indicators */}
      <div className="flex justify-center gap-2 mt-20 flex-wrap max-w-md mx-auto" role="tablist" aria-label="Screenshot navigation">
        {screenshots.map((screenshot, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            role="tab"
            aria-selected={index === currentIndex}
            aria-label={`View ${screenshot.title} screenshot`}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentIndex 
                ? 'w-8 bg-[#E10600]' 
                : 'bg-gray-400 hover:bg-gray-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default AppCarousel;
